import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Barbell,
    Confetti,
    DownloadSimple,
    Lightning,
    Medal,
    Stack,
    Timer,
    Trophy,
} from '@phosphor-icons/react';
import { toPng } from 'html-to-image';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { RECAP } from '@/constants/testIds';

function formatDuration(seconds) {
    const s = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) {
        return `${h}h ${String(m).padStart(2, '0')}m`;
    }
    return `${m}m ${String(sec).padStart(2, '0')}s`;
}

function formatVolume(v, units) {
    const n = Math.round(Number(v) || 0);
    return `${n.toLocaleString()} ${units}`;
}

export default function SessionRecap() {
    const { id: sessionId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const units = user?.units || 'kg';
    const [recap, setRecap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/sessions/${sessionId}/recap`);
                if (!cancelled) setRecap(data);
            } catch (e) {
                if (!cancelled)
                    setError(
                        formatApiErrorDetail(e.response?.data?.detail) || e.message
                    );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    const shareAsImage = useCallback(async () => {
        if (!cardRef.current || exporting) return;
        setExporting(true);
        try {
            const dataUrl = await toPng(cardRef.current, {
                cacheBust: true,
                pixelRatio: 2,
                // Force a solid dark background so the saved PNG looks correct
                // regardless of the page's theme (glass cards are translucent).
                backgroundColor: '#121212',
            });
            const a = document.createElement('a');
            const safe = (recap?.name || 'workout').replace(/[^\w\-]+/g, '_');
            a.download = `gymflow-${safe}.png`;
            a.href = dataUrl;
            a.click();
        } catch (e) {
            setError(`Couldn't export image: ${e.message}`);
        } finally {
            setExporting(false);
        }
    }, [recap, exporting]);

    if (loading) {
        return (
            <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-16 text-center">
                <p className="uppercase-mono text-muted-foreground animate-soft-pulse">
                    Loading recap…
                </p>
            </main>
        );
    }

    if (!recap) {
        return (
            <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-16 text-center">
                <p className="uppercase-mono text-muted-foreground">
                    {error || 'Session not found.'}
                </p>
                <Link
                    to="/workouts"
                    className="inline-flex items-center gap-1.5 mt-4 uppercase-mono text-foreground hover:underline"
                >
                    <ArrowLeft size={14} weight="bold" /> Back to workouts
                </Link>
            </main>
        );
    }

    const hasPRs = recap.pr_count > 0;
    const hadWork = recap.set_count > 0;

    return (
        <main
            data-testid={RECAP.root}
            className="mx-auto max-w-3xl px-5 sm:px-8 pt-10 sm:pt-14 pb-24"
        >
            <div className="animate-fade-up">
                <Link
                    to="/workouts"
                    className="inline-flex items-center gap-1.5 uppercase-mono text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft size={14} weight="bold" /> Back to workouts
                </Link>
                <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className="uppercase-mono text-muted-foreground">
                        Workout recap
                    </p>
                    {hadWork && (
                        <span className="inline-flex items-center gap-1.5 text-[#00FF9D] uppercase-mono">
                            <Confetti size={14} weight="fill" />
                            Logged
                        </span>
                    )}
                </div>
                <h1
                    data-testid={RECAP.title}
                    className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl mt-1"
                >
                    {recap.name}
                </h1>
            </div>

            {error && (
                <p className="mt-5 text-sm text-[#FF4D4D] font-medium">
                    {error}
                </p>
            )}

            {/* Shareable card — everything inside cardRef is captured by
                html-to-image. Wrapped in a solid bg so the PNG looks clean. */}
            <div
                ref={cardRef}
                data-testid={RECAP.shareCard}
                className="mt-7 rounded-3xl border border-foreground/10 bg-card p-6 sm:p-8 shadow-tactile relative overflow-hidden"
            >
                {/* Glow accent for PRs */}
                {hasPRs && (
                    <div
                        aria-hidden
                        className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF4D4D]/15 blur-3xl pointer-events-none"
                    />
                )}

                {/* Header strip */}
                <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <span className="grid place-items-center w-9 h-9 rounded-2xl bg-[#00FF9D]/15 border border-[#00FF9D]/30 text-[#00FF9D]">
                            <Barbell size={16} weight="bold" />
                        </span>
                        <p className="font-display font-extrabold tracking-tight text-base">
                            GYM
                            <span className="text-[#00FF9D]">FLOW</span>
                        </p>
                    </div>
                    <p className="uppercase-mono text-muted-foreground text-[10px] sm:text-xs">
                        {new Date(recap.started_at).toLocaleDateString(undefined, {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                        })}
                    </p>
                </div>

                {/* Headline numbers */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Tile
                        label="Volume"
                        valueTestId={RECAP.totalVolume}
                        value={formatVolume(recap.total_volume, units)}
                        Icon={Lightning}
                        accent="#00FF9D"
                    />
                    <Tile
                        label="Sets"
                        valueTestId={RECAP.setCount}
                        value={String(recap.set_count)}
                        Icon={Stack}
                    />
                    <Tile
                        label="Time"
                        valueTestId={RECAP.duration}
                        value={formatDuration(recap.duration_seconds)}
                        Icon={Timer}
                    />
                    <Tile
                        label="PRs"
                        valueTestId={RECAP.prCount}
                        value={String(recap.pr_count)}
                        Icon={Trophy}
                        accent={hasPRs ? '#FF4D4D' : undefined}
                    />
                </div>

                {/* Per-exercise breakdown */}
                <div className="mt-7">
                    <p className="uppercase-mono text-muted-foreground mb-3">
                        Top sets
                    </p>
                    {recap.exercises.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No completed sets this session.
                        </p>
                    ) : (
                        <ul
                            data-testid={RECAP.exerciseList}
                            className="space-y-2"
                        >
                            {recap.exercises.map((ex) => (
                                <li
                                    key={ex.exercise_id}
                                    data-testid={`${RECAP.exerciseRow}-${ex.exercise_id}`}
                                    className={`rounded-2xl border px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 ${
                                        ex.top_set?.is_pr
                                            ? 'border-[#FF4D4D]/30 bg-[#FF4D4D]/[0.06]'
                                            : 'border-foreground/10 bg-foreground/[0.02]'
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="font-display font-bold tracking-tight text-base truncate">
                                            {ex.name}
                                        </p>
                                        <p className="uppercase-mono text-muted-foreground">
                                            {ex.set_count}{' '}
                                            {ex.set_count === 1 ? 'set' : 'sets'}{' '}
                                            ·{' '}
                                            {formatVolume(ex.total_volume, units)}
                                        </p>
                                    </div>
                                    {ex.top_set && (
                                        <div className="text-right shrink-0">
                                            <p className="font-mono text-lg font-bold tabular-nums leading-tight">
                                                {ex.top_set.weight}
                                                <span className="uppercase-mono text-muted-foreground ml-1">
                                                    {units}
                                                </span>
                                                <span className="text-muted-foreground"> × </span>
                                                {ex.top_set.reps}
                                            </p>
                                            <p className="uppercase-mono text-muted-foreground leading-tight">
                                                e1RM {Math.round(ex.top_set.e1rm)}
                                                {units}
                                            </p>
                                        </div>
                                    )}
                                    {ex.top_set?.is_pr && (
                                        <span className="uppercase-mono inline-flex items-center gap-1 rounded-full px-2 py-1 border border-[#FF4D4D]/40 bg-[#FF4D4D]/15 text-[#FF4D4D]">
                                            <Medal size={11} weight="fill" />
                                            PR
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <p className="mt-6 uppercase-mono text-muted-foreground text-center">
                    gymflow · {recap.exercise_count} exercises
                </p>
            </div>

            {/* Action bar */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <button
                    type="button"
                    onClick={shareAsImage}
                    disabled={exporting}
                    data-testid={RECAP.shareBtn}
                    className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-semibold disabled:opacity-60"
                >
                    <DownloadSimple size={18} weight="bold" />
                    {exporting ? 'Exporting…' : 'Share as image'}
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/workouts')}
                    data-testid={RECAP.doneBtn}
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-2xl bg-foreground text-background font-bold tracking-tight hover:opacity-90 active:scale-[0.98] transition-all"
                >
                    Done
                </button>
            </div>
        </main>
    );
}

function Tile({ label, value, valueTestId, Icon, accent }) {
    return (
        <div
            className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3"
            style={accent ? { borderColor: `${accent}33` } : undefined}
        >
            <p className="uppercase-mono text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                {Icon && (
                    <Icon
                        size={12}
                        weight="bold"
                        style={accent ? { color: accent } : undefined}
                    />
                )}
                {label}
            </p>
            <p
                data-testid={valueTestId}
                className="font-mono text-2xl font-extrabold tabular-nums tracking-tight"
                style={accent ? { color: accent } : undefined}
            >
                {value}
            </p>
        </div>
    );
}
